/**
 * Licensed Materials - Property of IBM
 * IBM Watson Analytics (C) Copyright IBM Corp. 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import $ from 'jquery';
import { handleScroll } from '../utils';
const SLOT_ITEM_TOUCH_DRAG_DURATION = 600;

class DndSlotItemHandler {
	/**
	 * DndSlotItemHandler hides most of the drag and drop handling used by the glass DndManager
	 * It handles drop of data from the metadata tree to a Slot Item (or Slot Heading)
	 * It also handles Drag&Drop of slot to slot data.
	 *
	 * @param {DOMNode} domNode - DOM node to attach drag and drop to
	 * @param {string} className - class name
	 * @param {Function} dropHandler - function to be called on drop
	 * @param {Function} [acceptsHandler] - function to be used as validation for accepting the drag event as drop target; by default always accepts; pass custom function for additional validation
	 */
	constructor(domNode, className, dropHandler, acceptsHandler = () => true, dndManager) {
		this.domNode = domNode;
		this.className = className;
		this.dropHandler = dropHandler;
		this.acceptsHandler = acceptsHandler;
		this.timer = null;
		this.dndManager = dndManager;
	}

	/**
	 * A slotItem can accept data from the metadata tree/data grid (type MODEL_ITEM)
	 * or from another slot Item (ie: swap 2 slot items or move items from one slot to another.)
	 * A slotItem also accepts a shape (type widget) if the visualization type is a summary
	 */
	accepts(dragObject) {
		return (
			(
				dragObject.type === 'SLOT_ITEM' || dragObject.widgetType === 'shape' ||
				((dragObject.type === 'GRID_HEADER_ITEM' || dragObject.type === 'MODEL_ITEM') &&
					dragObject.data && dragObject.data.utils && dragObject.data.utils.isValid()
				)
			) &&
			this.acceptsHandler(dragObject)
		);
	}

	/**
	 * @param dragObject - the dragObject (that could have come from the metadata tree, another slot item, or a shape)
	 * @returns the data portion of the payload - one of: source dataItemHandler, metadatacolumns, or shape widget
	 */
	getData(dragObject) {

		if (!dragObject) {
			return null;
		}
		if (dragObject.type === 'SLOT_ITEM') {
			return dragObject.data.source;
		}
		if (dragObject.widgetType === 'shape') {
			return dragObject;
		}
		return this._getDropMetadataColumns(dragObject);
	}

	_getDropMetadataColumns(dragObject) {
		return dragObject && dragObject.data;
	}

	activateDropTarget() {
		$(this.domNode).toggleClass(this.className, true);
	}

	deactivateDropTarget() {
		$(this.domNode).toggleClass(this.className, false);
	}

	onDragStart() {}

	onDragMove(dragObject, dropNode, event) {
		const slotEditorNode = $(dropNode).closest('.slotEditor').get(0);
		if(slotEditorNode) {
			handleScroll(event, slotEditorNode, () => this.dndManager && this.dndManager.reassessContainer(slotEditorNode));
		}
		this.activateDropTarget();
	}

	onDragLeave() {
		this.deactivateDropTarget();
	}

	onDragEnd() {
		this.deactivateDropTarget();
	}

	onDrop(dragObject, dropNode) {
		this.dropHandler(dragObject, dropNode);
	}

	/**
	 * @param dndManager - the dndManager to initialize the drag within
	 * @param cbGetSlotDataItemHandler - a callback function which should be called when starting any drag to get the up-to-date item data handler.
	 */
	initializeDndSource(dndManager, cbGetSlotDataItemHandler, iconsFeature) {
		// Separate mouse and touch event handlers for defect 241719
		$(this.domNode).on('mousedown', {}, (event) => {
			this._startDrag({
				dndManager: dndManager,
				event: event,
				cbGetSlotDataItemHandler: cbGetSlotDataItemHandler,
				iconsFeature
			});
			return true;
		});

		$(this.domNode).on('touchstart', {}, (event) => {
			this._touchStart({
				dndManager: dndManager,
				event: event,
				cbGetSlotDataItemHandler: cbGetSlotDataItemHandler,
				iconsFeature
			});
			return true;
		});

		$(this.domNode).on('touchend touchcancel', {}, () => this._touchEnd());
	}

	// defect 241719
	// after touchstart, wait short time to start drag process if still touch,
	// so that it can reduce change of ambiguous event with scrolling of the slot editor
	_touchStart(options) {
		this.timer = setTimeout(function() {
			this._touchStartDrag(options);
		}.bind(this), SLOT_ITEM_TOUCH_DRAG_DURATION);
	}

	_touchEnd() {
		if (this.timer) {
			clearTimeout(this.timer);
		}
		this.timer = null;
	}

	_touchStartDrag(options) {
		if (this.timer) {
			this._startDrag(options);
		}
	}

	_startDrag(options) {
		let $avatar = $('<div style="position:absolute; z-index:10000;"/>');

		const $draggedNode = $(this.domNode).clone().addClass('draggedNode');
		if(options.iconsFeature) {
			const disableIcon = options.iconsFeature.getIcon('common-nodrop');
			if(disableIcon) {
				const $nodropSign = $(
					`<div class="slotEditorItem__nameLine__nodrop is-hidden">
						<svg class="svgIcon">
							<use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="#${disableIcon.id}">
							</use>
						</svg>
					</div>`
				);
				$draggedNode.find('.slotEditorItem__nameLine').prepend($nodropSign);
			}
		}
		$avatar.append($draggedNode);

		options.dndManager.startDrag({
			event: options.event,
			type: 'SLOT_ITEM',
			data: {
				source: options.cbGetSlotDataItemHandler()
			},
			callerCallbacks: {
				onMove: this._onMove.bind(this)
			},
			avatar: $avatar,
			moveXThreshold: 20,
			moveYThreshold: 20
		});
	}

	_onMove(event, payload = {}) {
		const { dragObject } = payload;
		if(dragObject) {
			const { avatar } = dragObject;
			if (avatar) {
				const $avatar = $(avatar);
				const $nodrop = $avatar.find('.slotEditorItem__nameLine__nodrop');
				if($nodrop.length) {
					const { dropTargetNode } = payload;
					if(!dropTargetNode) {
						// no payload.dropTargetNode means currenly you cannot drop here, so we show `not allowed drop sign`
						$nodrop.toggleClass('is-hidden', false);
					} else {
						// else payload.dropTargetNode exists,
						// if the drop target is not `.slotEditor` (which is used for drag auto scroll ),
						// then means currenly you can REALLY drop the slot item here, so we does not show `not allowed drop sign`
						const isDropTargetNotSlotEditor = !(dropTargetNode.classList && dropTargetNode.classList.contains('slotEditor'));
						$nodrop.toggleClass('is-hidden', isDropTargetNotSlotEditor);
					}
				}
			}
		}
	}
}

export default DndSlotItemHandler;



// WEBPACK FOOTER //
// ./src/js/OldSlotEditorComponents/DndSlotItemHandler.js